import { EmbeddingModel } from './embedding.consts';
import { Llm, SupportedLlm } from './llm.consts';
import { SupportedModel } from './model.consts';
import { TranscriptionModel } from './transcription.consts';

/**
 * These maps are used to convert between the Kindo Llm enum and the LiteLLM model name.
 *
 * IMPORTANT:
 * 1. Add litellm model strings (model_name value in litellm config) to LITELLM_MODEL_STRINGS
 *     a. In the litellm repo, model_name is the name to pass to litellm from external clients
 * 2. Add a mapping from the Llm enum to the litellm model string in LLM_TO_LITELLM_MODELS
 * 3. Any updates to LLM_TO_LITELLM_MODELS must be mirrored in the LITELLM_MODELS_TO_LLM map below.
 * 4. Some Llm's map to multiple LiteLLM models due to the need to support backwards compatibility
 *    for older Kindo versions that may use the old LiteLLM model names.
 */
export enum LitellmModel {
  CLAUDE_3_5_HAIKU = 'claude-3-5-haiku',
  CLAUDE_3_5_SONNET = 'claude-3-5-sonnet',
  CLAUDE_3_5_SONNET_20240620 = 'claude-3-5-sonnet-20240620',
  CLAUDE_3_7_SONNET = 'claude-3-7-sonnet',
  CLAUDE_3_OPUS = 'claude-3-opus',
  CLAUDE_3_OPUS_20240229 = 'claude-3-opus-20240229',
  DEEPSEEK_R1 = 'deepseek-r1',
  GEMINI_2_0_FLASH = 'gemini-2.0-flash',
  GEMINI_2_0_FLASH_LITE = 'gemini-2.0-flash-lite',
  GEMINI_2_5_PRO = 'gemini-2.5-pro',
  GPT_4O = 'azure/gpt-4o',
  GPT_4_1 = 'gpt-4.1',
  GPT_4_1_MINI = 'gpt-4.1-mini',
  /**
   * Start internal LLMs
   *
   * Notes:
   * INTERNAL_LARGE_WORKER, INTERNAL_SMALL_WORKER: In the production cluster (us-west1), we have the following self-hosted models: Llama 3.1 8B and 3.1 70B.
   *    In the staging cluster (us-central1) and local development, we use the Azure GPT-4o mini model
   *    because self-hosting the model in these environments is not cost effective.
   * INTERNAL_AUTO_GENERATION: In the production cluster (us-west1), we have a self-hosted Llama 3.1 8B model.
   *    In the staging cluster (us-central1) and local development, we use the Azure GPT-3.5 Turbo model
   *    because self-hosting the model in these environments is not cost effective.
   */
  INTERNAL_AUTO_GENERATION = 'auto-generation-llm',
  INTERNAL_EMBEDDING_GENERATOR = 'embedding-generator',
  INTERNAL_INGESTION_EXTRACTION = 'ingestion-extraction-llm',
  INTERNAL_LARGE_WORKER = 'large-worker-llm',
  INTERNAL_SMALL_WORKER = 'small-worker-llm',
  INTERNAL_TRANSCRIPTION_GENERATOR = 'transcription-generator',
  /** End internal LLMs */
  LLAMA_3_1_8B_INSTANT = 'llama-3.1-8b-instant',
  LLAMA_3_3_70B_VERSATILE = 'llama-3.3-70b-versatile',
  O1 = 'o1',
  O3 = 'o3',
  O4_MINI = 'o4-mini',
  QWEN2_5_72B_INSTRUCT_TURBO = 'qwen/qwen2.5-72b-instruct-turbo',
  QWEN2_72B_INSTRUCT = 'qwen/qwen2-72b-instruct',
  WHITERABBITNEO_2_5_QWEN_2_5_32B = 'WhiteRabbitNeo/WhiteRabbitNeo-2.5-Qwen-2.5-32B',
  WHITERABBITNEO_33B_DEEPSEEK_CODER = '/models/WhiteRabbitNeo-33B-DeepSeekCoder',
  WHITERABBITNEO_V3_7B = 'WhiteRabbitNeo/WhiteRabbitNeo-V3-7B'
}

export const isLitellmModel = (value: string): value is LitellmModel =>
  Object.values(LitellmModel).includes(value as LitellmModel);

export const MODEL_TO_LITELLM_MODELS: Record<SupportedModel, LitellmModel> = {
  [Llm.CLAUDE_3_5_SONNET]: LitellmModel.CLAUDE_3_5_SONNET,
  [Llm.CLAUDE_3_7_SONNET]: LitellmModel.CLAUDE_3_7_SONNET,
  [Llm.CLAUDE_3_OPUS]: LitellmModel.CLAUDE_3_OPUS,
  [Llm.CLAUDE_3_5_HAIKU]: LitellmModel.CLAUDE_3_5_HAIKU,
  [Llm.DEEPSEEK_R1]: LitellmModel.DEEPSEEK_R1,
  [Llm.GEMINI_2_0_FLASH]: LitellmModel.GEMINI_2_0_FLASH,
  [Llm.GEMINI_2_0_FLASH_LITE]: LitellmModel.GEMINI_2_0_FLASH_LITE,
  [Llm.GEMINI_2_5_PRO]: LitellmModel.GEMINI_2_5_PRO,
  [Llm.GPT_4O]: LitellmModel.GPT_4O,
  [Llm.GPT_4_1]: LitellmModel.GPT_4_1,
  [Llm.GPT_4_1_MINI]: LitellmModel.GPT_4_1_MINI,
  /** Start internal LLMs */
  [EmbeddingModel.INTERNAL_EMBEDDING_GENERATOR]:
    LitellmModel.INTERNAL_EMBEDDING_GENERATOR,
  [Llm.INTERNAL_AUTO_GENERATION]: LitellmModel.INTERNAL_AUTO_GENERATION,
  [Llm.INTERNAL_INGESTION_EXTRACTION]:
    LitellmModel.INTERNAL_INGESTION_EXTRACTION,
  [Llm.INTERNAL_LARGE_WORKER]: LitellmModel.INTERNAL_LARGE_WORKER,
  [Llm.INTERNAL_SMALL_WORKER]: LitellmModel.INTERNAL_SMALL_WORKER,
  [TranscriptionModel.INTERNAL_TRANSCRIPTION_GENERATOR]:
    LitellmModel.INTERNAL_TRANSCRIPTION_GENERATOR,
  /** End internal LLMs */
  [Llm.LLAMA_3_3_70B]: LitellmModel.LLAMA_3_3_70B_VERSATILE,
  [Llm.LLAMA_3_1_8B_INSTANT]: LitellmModel.LLAMA_3_1_8B_INSTANT,
  [Llm.O1]: LitellmModel.O1,
  [Llm.O3]: LitellmModel.O3,
  [Llm.O4_MINI]: LitellmModel.O4_MINI,
  [Llm.QWEN2_72B_INSTRUCT]: LitellmModel.QWEN2_72B_INSTRUCT,
  [Llm.QWEN2_5_72B_INSTRUCT_TURBO]: LitellmModel.QWEN2_5_72B_INSTRUCT_TURBO,
  [Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B]:
    LitellmModel.WHITERABBITNEO_2_5_QWEN_2_5_32B,
  [Llm.WHITERABBITNEO_33B]: LitellmModel.WHITERABBITNEO_33B_DEEPSEEK_CODER,
  [Llm.WHITERABBITNEO_V3_7B]: LitellmModel.WHITERABBITNEO_V3_7B
};

// Some Llm's map to multiple LiteLLM models due to the need to support backwards compatibility
// for older Kindo versions that may use the old LiteLLM model names.
export const LITELLM_MODELS_TO_MODEL: Record<LitellmModel, SupportedModel> = {
  [LitellmModel.CLAUDE_3_5_SONNET]: Llm.CLAUDE_3_5_SONNET,
  [LitellmModel.CLAUDE_3_7_SONNET]: Llm.CLAUDE_3_7_SONNET,
  [LitellmModel.CLAUDE_3_5_HAIKU]: Llm.CLAUDE_3_5_HAIKU,
  [LitellmModel.CLAUDE_3_5_SONNET_20240620]: Llm.CLAUDE_3_5_SONNET,
  [LitellmModel.CLAUDE_3_OPUS]: Llm.CLAUDE_3_OPUS,
  [LitellmModel.CLAUDE_3_OPUS_20240229]: Llm.CLAUDE_3_OPUS,
  [LitellmModel.DEEPSEEK_R1]: Llm.DEEPSEEK_R1,
  [LitellmModel.GEMINI_2_0_FLASH]: Llm.GEMINI_2_0_FLASH,
  [LitellmModel.GEMINI_2_0_FLASH_LITE]: Llm.GEMINI_2_0_FLASH_LITE,
  [LitellmModel.GEMINI_2_5_PRO]: Llm.GEMINI_2_5_PRO,
  [LitellmModel.GPT_4O]: Llm.GPT_4O,
  [LitellmModel.GPT_4_1]: Llm.GPT_4_1,
  [LitellmModel.GPT_4_1_MINI]: Llm.GPT_4_1_MINI,
  /** Start internal LLMs */
  [LitellmModel.INTERNAL_AUTO_GENERATION]: Llm.INTERNAL_AUTO_GENERATION,
  [LitellmModel.INTERNAL_EMBEDDING_GENERATOR]:
    EmbeddingModel.INTERNAL_EMBEDDING_GENERATOR,
  [LitellmModel.INTERNAL_INGESTION_EXTRACTION]:
    Llm.INTERNAL_INGESTION_EXTRACTION,
  [LitellmModel.INTERNAL_LARGE_WORKER]: Llm.INTERNAL_LARGE_WORKER,
  [LitellmModel.INTERNAL_SMALL_WORKER]: Llm.INTERNAL_SMALL_WORKER,
  [LitellmModel.INTERNAL_TRANSCRIPTION_GENERATOR]:
    TranscriptionModel.INTERNAL_TRANSCRIPTION_GENERATOR,
  /** End internal LLMs */
  [LitellmModel.LLAMA_3_1_8B_INSTANT]: Llm.LLAMA_3_1_8B_INSTANT,
  [LitellmModel.LLAMA_3_3_70B_VERSATILE]: Llm.LLAMA_3_3_70B,
  [LitellmModel.O1]: Llm.O1,
  [LitellmModel.O3]: Llm.O3,
  [LitellmModel.O4_MINI]: Llm.O4_MINI,
  [LitellmModel.QWEN2_72B_INSTRUCT]: Llm.QWEN2_72B_INSTRUCT,
  [LitellmModel.QWEN2_5_72B_INSTRUCT_TURBO]: Llm.QWEN2_5_72B_INSTRUCT_TURBO,
  [LitellmModel.WHITERABBITNEO_2_5_QWEN_2_5_32B]:
    Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B,
  [LitellmModel.WHITERABBITNEO_33B_DEEPSEEK_CODER]: Llm.WHITERABBITNEO_33B,
  [LitellmModel.WHITERABBITNEO_V3_7B]: Llm.WHITERABBITNEO_V3_7B
};

export function getLitellmModelFromSupportedModel(
  model: SupportedModel
): LitellmModel {
  return MODEL_TO_LITELLM_MODELS[model];
}

//
// LLM_CONTEXT_WINDOW values are taken from LiteLLM's model_prices_and_context_window.json.

// IMPORTANT: Values in this map must match values in the MODEL_TO_LITELLM_WITH_CONTEXT_WINDOW const and packages/prisma/src/scripts/backfillModel.ts
//            If you update this map, you must also update the MODEL_TO_LITELLM_WITH_CONTEXT_WINDOW const (below) and backfillModel.ts file.
//
export const LLM_CONTEXT_WINDOW: Record<SupportedLlm, number> = {
  [Llm.CLAUDE_3_OPUS]: 200 * 1000,
  [Llm.CLAUDE_3_5_HAIKU]: 200 * 1000,
  [Llm.CLAUDE_3_5_SONNET]: 200 * 1000,
  [Llm.CLAUDE_3_7_SONNET]: 200 * 1000,
  [Llm.DEEPSEEK_R1]: 128 * 1000,
  [Llm.GEMINI_2_0_FLASH]: 1048576,
  [Llm.GEMINI_2_0_FLASH_LITE]: 1048576,
  [Llm.GEMINI_2_5_PRO]: 1048576,
  [Llm.GPT_4O]: 128 * 1000,
  [Llm.GPT_4_1]: 1047576,
  [Llm.GPT_4_1_MINI]: 1047576,
  // # Start internal LLMs
  // # context window = 0 since these models (and therefore their context windows) are set by environment variables
  // # so we default to llama index's default context window
  [Llm.INTERNAL_AUTO_GENERATION]: 0,
  [Llm.INTERNAL_INGESTION_EXTRACTION]: 0,
  [Llm.INTERNAL_LARGE_WORKER]: 0,
  [Llm.INTERNAL_SMALL_WORKER]: 0,
  // # End internal LLMs
  [Llm.LLAMA_3_1_8B_INSTANT]: 128 * 1000,
  [Llm.LLAMA_3_3_70B]: 128 * 1000,
  [Llm.O1]: 200 * 1000,
  [Llm.O3]: 200 * 1000,
  [Llm.O4_MINI]: 200 * 1000,
  [Llm.QWEN2_72B_INSTRUCT]: 32768,
  [Llm.QWEN2_5_72B_INSTRUCT_TURBO]: 32768,
  [Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B]: 128 * 1000,
  [Llm.WHITERABBITNEO_33B]: 16 * 1000,
  [Llm.WHITERABBITNEO_V3_7B]: 32768
};

export interface LlmWithContextWindow {
  contextWindow: number | null;
  litellmModelName: string;
}
export const MODEL_TO_LITELLM_WITH_CONTEXT_WINDOW: Record<
  SupportedLlm,
  LlmWithContextWindow
> = {
  [Llm.CLAUDE_3_OPUS]: {
    litellmModelName: LitellmModel.CLAUDE_3_OPUS,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.CLAUDE_3_OPUS]
  },

  [Llm.CLAUDE_3_5_SONNET]: {
    litellmModelName: LitellmModel.CLAUDE_3_5_SONNET,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.CLAUDE_3_5_SONNET]
  },
  [Llm.CLAUDE_3_5_HAIKU]: {
    litellmModelName: LitellmModel.CLAUDE_3_5_HAIKU,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.CLAUDE_3_5_HAIKU]
  },
  [Llm.CLAUDE_3_7_SONNET]: {
    litellmModelName: LitellmModel.CLAUDE_3_7_SONNET,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.CLAUDE_3_7_SONNET]
  },
  [Llm.DEEPSEEK_R1]: {
    litellmModelName: LitellmModel.DEEPSEEK_R1,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.DEEPSEEK_R1]
  },
  [Llm.GEMINI_2_0_FLASH]: {
    litellmModelName: LitellmModel.GEMINI_2_0_FLASH,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.GEMINI_2_0_FLASH]
  },
  [Llm.GEMINI_2_0_FLASH_LITE]: {
    litellmModelName: LitellmModel.GEMINI_2_0_FLASH_LITE,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.GEMINI_2_0_FLASH_LITE]
  },
  [Llm.GEMINI_2_5_PRO]: {
    litellmModelName: LitellmModel.GEMINI_2_5_PRO,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.GEMINI_2_5_PRO]
  },
  [Llm.GPT_4O]: {
    litellmModelName: LitellmModel.GPT_4O,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.GPT_4O]
  },
  [Llm.GPT_4_1]: {
    litellmModelName: LitellmModel.GPT_4_1,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.GPT_4_1]
  },
  [Llm.GPT_4_1_MINI]: {
    litellmModelName: LitellmModel.GPT_4_1_MINI,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.GPT_4_1_MINI]
  },
  /** Start internal LLMs */
  [Llm.INTERNAL_AUTO_GENERATION]: {
    litellmModelName: LitellmModel.INTERNAL_AUTO_GENERATION,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.INTERNAL_AUTO_GENERATION]
  },
  [Llm.INTERNAL_INGESTION_EXTRACTION]: {
    litellmModelName: LitellmModel.INTERNAL_INGESTION_EXTRACTION,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.INTERNAL_INGESTION_EXTRACTION]
  },
  [Llm.INTERNAL_LARGE_WORKER]: {
    litellmModelName: LitellmModel.INTERNAL_LARGE_WORKER,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.INTERNAL_LARGE_WORKER]
  },
  [Llm.INTERNAL_SMALL_WORKER]: {
    litellmModelName: LitellmModel.INTERNAL_SMALL_WORKER,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.INTERNAL_SMALL_WORKER]
  },
  /** End internal LLMs */
  [Llm.LLAMA_3_1_8B_INSTANT]: {
    litellmModelName: LitellmModel.LLAMA_3_1_8B_INSTANT,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.LLAMA_3_1_8B_INSTANT]
  },
  [Llm.LLAMA_3_3_70B]: {
    litellmModelName: LitellmModel.LLAMA_3_3_70B_VERSATILE,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.LLAMA_3_3_70B]
  },
  [Llm.O1]: {
    litellmModelName: LitellmModel.O1,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.O1]
  },
  [Llm.O3]: {
    litellmModelName: LitellmModel.O3,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.O3]
  },
  [Llm.O4_MINI]: {
    litellmModelName: LitellmModel.O4_MINI,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.O4_MINI]
  },
  [Llm.QWEN2_72B_INSTRUCT]: {
    litellmModelName: LitellmModel.QWEN2_72B_INSTRUCT,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.QWEN2_72B_INSTRUCT]
  },
  [Llm.QWEN2_5_72B_INSTRUCT_TURBO]: {
    litellmModelName: LitellmModel.QWEN2_5_72B_INSTRUCT_TURBO,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.QWEN2_5_72B_INSTRUCT_TURBO]
  },
  [Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B]: {
    litellmModelName: LitellmModel.WHITERABBITNEO_2_5_QWEN_2_5_32B,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B]
  },
  [Llm.WHITERABBITNEO_33B]: {
    litellmModelName: LitellmModel.WHITERABBITNEO_33B_DEEPSEEK_CODER,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.WHITERABBITNEO_33B]
  },
  [Llm.WHITERABBITNEO_V3_7B]: {
    litellmModelName: LitellmModel.WHITERABBITNEO_V3_7B,
    contextWindow: LLM_CONTEXT_WINDOW[Llm.WHITERABBITNEO_V3_7B]
  }
};
